import { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBoxesStacked,
  faClipboardCheck,
  faObjectGroup,
  faScissors,
  faShirt,
  faTruckFast,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

import { decimal, integer } from '@/components/report/groupedColumns';
import { Skeleton } from '@/components/ui/skeleton';

import { SEGMENT, SegmentCard } from './segments';
import type { SummaryRow } from './summary';

/**
 * Range-total cards for the Detail / Summary tabs — legacy CARD_DEFS in productionOutput_v2.js.
 *
 * Straight presentation sums of the long-form summary result (§4.3); SQL supplies the weighted
 * range material-cost percentage, React never derives that ratio. Not `<StatTile>`s — these
 * figures have no target and carry no status, so nothing here is shaded (§4.6).
 */

/** The six operations, in legacy CARD_DEFS order. */
const CARDS: readonly { name: string; icon: IconDefinition }[] = [
  { name: 'Cutting', icon: faScissors },
  { name: 'Pairing', icon: faObjectGroup },
  { name: 'Supermarket', icon: faBoxesStacked },
  { name: 'Sewing', icon: faShirt },
  { name: 'QC', icon: faClipboardCheck },
  { name: 'Shipment', icon: faTruckFast },
];

const DETAILED = new Set(['Sewing', 'QC', 'Shipment']);

/** One secondary measure. `flex-1` spreads the rows over the stretched segment height. */
function Metric({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-1 items-baseline justify-between gap-2 py-1 first:pt-0 last:pb-0">
      <dt className="min-w-0 text-xs text-muted-foreground">{label}</dt>
      <dd className="shrink-0 text-sm font-semibold tabular-nums">{value}</dd>
    </div>
  );
}

interface CardTotals {
  pieces: number;
  produced_mins: number | null;
  revenue_hkd: number | null;
  material_cost_hkd: number | null;
  material_cost_pct: number | null;
}

function rangeTotals(rows: SummaryRow[]): Map<string, CardTotals> {
  const map = new Map<string, CardTotals>();
  for (const { name } of CARDS) {
    map.set(name, {
      pieces: 0,
      produced_mins: DETAILED.has(name) ? 0 : null,
      revenue_hkd: DETAILED.has(name) ? 0 : null,
      material_cost_hkd: DETAILED.has(name) ? 0 : null,
      material_cost_pct: null,
    });
  }

  for (const r of rows) {
    const card = map.get(r.operation);
    if (card === undefined) continue;
    card.pieces += r.pieces ?? 0;
    if (DETAILED.has(r.operation)) {
      card.produced_mins = (card.produced_mins ?? 0) + (r.produced_mins ?? 0);
      card.revenue_hkd = (card.revenue_hkd ?? 0) + (r.revenue_hkd ?? 0);
      card.material_cost_hkd = (card.material_cost_hkd ?? 0) + (r.material_cost_hkd ?? 0);
      card.material_cost_pct = r.range_material_cost_pct;
    }
  }

  return map;
}

export function OutputCards({
  rows,
  isLoading,
  truncated = false,
}: {
  rows: SummaryRow[];
  isLoading: boolean;
  truncated?: boolean;
}) {
  const byOperation = useMemo(() => rangeTotals(rows), [rows]);

  if (isLoading) {
    return (
      <SegmentCard>
        {CARDS.map(({ name }) => (
          <div key={name} className={SEGMENT}>
            <Skeleton className="h-24 w-full" />
          </div>
        ))}
      </SegmentCard>
    );
  }

  if (truncated || rows.length === 0) return null; // no range total over a capped result (§4.3)

  return (
    <SegmentCard>
      {CARDS.map(({ name, icon }) => {
        const row = byOperation.get(name);
        const detailed = row !== undefined && DETAILED.has(name);
        return (
          <div key={name} className={SEGMENT}>
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <FontAwesomeIcon icon={icon} className="size-3" aria-hidden />
              {name}
            </span>

            <span className="mt-0.5 block">
              <span className="text-2xl leading-tight font-semibold tabular-nums">
                {row === undefined ? '—' : integer.format(Math.round(row.pieces))}
              </span>
            </span>

            <span className="mt-0.5 text-xs text-muted-foreground">Quantity</span>

            {detailed ? (
              <dl className="mt-1.5 flex flex-1 flex-col divide-y divide-border/60">
                <Metric
                  label="Produced mins"
                  value={
                    row.produced_mins === null
                      ? '—'
                      : integer.format(Math.round(row.produced_mins))
                  }
                />
                <Metric
                  label="Revenue"
                  value={
                    row.revenue_hkd === null ? '—' : integer.format(Math.round(row.revenue_hkd))
                  }
                />
                <Metric
                  label="Material cost"
                  value={
                    row.material_cost_hkd === null
                      ? '—'
                      : integer.format(Math.round(row.material_cost_hkd))
                  }
                />
                <Metric
                  label="Material cost %"
                  value={
                    row.material_cost_pct === null
                      ? '—'
                      : `${decimal.format(row.material_cost_pct)}%`
                  }
                />
              </dl>
            ) : null}
          </div>
        );
      })}
    </SegmentCard>
  );
}
